import { ACCESS } from './../../data/access-map';
import { SignalRMessage } from './../../../Models/signalR.message';
import { AppHelper } from './../../appHelper/appHelper';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Subject, Observable, ReplaySubject, of as observableOf } from 'rxjs';
import * as signalR from "@aspnet/signalr";
import { filter, flatMap, finalize, share } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { LocalRepository } from '../../data/repository/local-repository';

@Injectable({
    providedIn: 'root' // Angular 6 easier provider registration
})
export class SignalRService {

    allNotifications: Subject<SignalRMessage> = new Subject<SignalRMessage>();

    private _hubConnection: signalR.HubConnection;
    private _connectionSubject: ReplaySubject<void>;
    private _subjects: { [key: string]: Subject<SignalRMessage> } = {};

    constructor(
        @Inject(PLATFORM_ID) private platformId,
        private repo: LocalRepository
    ) {
    }

    startConnection(): Observable<void> {
        if (!isPlatformBrowser(this.platformId)) {
            return observableOf();
        }
        if (this._connectionSubject && !this._connectionSubject.isStopped) {
            return this._connectionSubject.pipe(share());
        }

        this._connectionSubject = new ReplaySubject<void>(1);
        this._hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(AppHelper.signalR_Url, { accessTokenFactory: () => this.repo.getItem('access_token') })
            // .withUrl('http://localhost:13485/SocketUser', { accessTokenFactory: () => this.repo.getItem('access_token') })//DEBUG (IT TAKES A LOT OF TIME BEHIND THE WDS PROXY)
            .build();

        this._hubConnection.on('notify', (data: SignalRMessage) => {
            console.log('SignalR: Received message: ', data);
            this.allNotifications.next(data);

            var subj = this.getRelevantSubject(data.type, data.subType);
            subj.next(data);
        });

        this._hubConnection.onclose(err => {
            this._connectionSubject.complete();
            if (err) { this.startConnection(); }
        });

        // console.log('Connecting to SignalR...');
        this._hubConnection
            .start()
            .then(() => {
                // console.log('SignalR Connection started');
                this._connectionSubject.next();
            })
            .catch(err => {
                if (err) {
                    // console.log('Error while starting SignalR connection. Retrying in a minute...');
                    this._connectionSubject.complete();
                    setTimeout(() => {
                        this.startConnection();
                    }, 60000);
                }
            });

        return this._connectionSubject.pipe(share());
    }

    isConnected = () => this._hubConnection && this._hubConnection.state === signalR.HubConnectionState.Connected;

    private buildKey(type: string, subType?: string, id?: number) {
        var result = type;
        if (subType) { result += ':' + subType; }
        if (id) { result += ':' + id; }
        return result;
    }

    /** Note: we don't create a subject for each id. Instead we use pipe(filter((res: SignalRMessage) => res.id === id)) */
    private getRelevantSubject(type: string, subType?: string): Subject<SignalRMessage> {
        var key = this.buildKey(type, subType);
        var subject = this._subjects[key];
        if (subject) { return subject; }
        subject = this._subjects[key] = new Subject<SignalRMessage>();
        return subject;
    }

    on(type: string, subType?: string, id?: number): Observable<SignalRMessage> {
        if (!isPlatformBrowser(this.platformId)) {
            return observableOf();
        }
        const subscribeMethod = ACCESS.SayehAdmin ? 'SubscribeAdmin' : 'Subscribe';
        return this.startConnection().pipe(flatMap(() => {
            //  console.log(`SignalR: Subscribing to ${this.buildKey(type, subType, id)}`);
            this._hubConnection.invoke(subscribeMethod,
                {
                    type: type,
                    subType: subType,
                    id: id,
                });

            var obs = this.getRelevantSubject(type, subType).pipe(finalize(() => {
                // console.log(`SignalR: Unsubscribing from ${this.buildKey(type, subType, id)}`);
                this._hubConnection.invoke('Unsubscribe',
                    {
                        type: type,
                        subType: subType,
                        id: id,
                    });
            }));
            if (id) { obs = obs.pipe(filter((res: SignalRMessage) => res.id === id)); }
            return obs;
        }));
    }

}
